import React, { useEffect, useRef, useState, useCallback } from 'react'
import { Wrapper } from '../style'
import { Table, Modal, Input, Select, message } from 'antd';
import { MakeApiCall } from "../../../../../apis";
import { useDispatch } from 'react-redux';
import { setData as setDataAction } from '../../../../../redux/actions';

const { Option } = Select;

// ── Moved OUTSIDE to prevent remount/re-render loops ──
const ProfileSelectionModal = ({ isOpen, onCancel, adProfiles, selectedAdProfileId, setSelectedAdProfileId, onSave }) => (
  <Modal
    title="Select Advertising Profile"
    open={isOpen}
    onCancel={onCancel}
    footer={[
      <button key="back" className="btn btn-light fs-7 fw-bold" onClick={onCancel}>Cancel</button>,
      <button key="submit" className="btn btn-danger fs-7 fw-bold ms-3" disabled={!selectedAdProfileId} onClick={onSave}>
        Save Selected Profile
      </button>
    ]}
  >
    <div className="mb-3">
      <label className="mb-2">Advertising Profile</label>
      <Select
        style={{ width: "100%" }}
        value={selectedAdProfileId}
        onChange={setSelectedAdProfileId}
        placeholder="Select Ad Profile"
      >
        {adProfiles.map(p => (
          <Option key={p.profileId} value={p.profileId.toString()}>
            {p.accountInfo?.name} ({p.countryCode}) – {p.profileId} ({p.accountInfo?.type})
          </Option>
        ))}
      </Select>
    </div>
  </Modal>
);

function AmazonAdvertising({ loading, list, userData, fetchList }) {
  const dispatch = useDispatch();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const refreshAndUpdateAdsFlag = useCallback(async () => {
    try {
      let records = null;
      if (typeof fetchList === 'function') {
        try { records = await fetchList(); } catch (e) { records = null; console.warn('fetchList threw, falling back to API call in refreshAndUpdateAdsFlag', e); }
      }
      if (!records) {
        const resp = await MakeApiCall('user-credentials', 'get', null, true, {}, userData?.token);
        if (resp?.status) records = resp?.data?.records || {};
      }
      const adsList = (records && records['Advertising-API']) || [];
      const hasAds = Array.isArray(adsList) && adsList.length > 0;
      if (userData) {
        const currentFlag = userData?.user?.user_data?.is_amz_ads_connected === 1;
        if (currentFlag !== hasAds) {
          const newUserData = {
            ...userData,
            user: {
              ...userData.user,
              user_data: {
                ...userData.user.user_data,
                is_amz_ads_connected: hasAds ? 1 : 0,
              },
            },
          };
          try { dispatch(setDataAction(newUserData)); } catch (err) { console.error('Error dispatching setDataAction in refreshAndUpdateAdsFlag:', err); }
        }
      }
    } catch (err) {
      console.error('Error fetching credentials for refreshAndUpdateAdsFlag:', err);
    }
  }, [userData, dispatch]);

  const clientId = "amzn1.application-oa2-client.c2d27c6ff2ab4e36a32810c448b9820f";
  const redirectUri = "https://app.sellerview.ai/settings/marketplace-credentials?tab=advertising";
  const scope = "advertising::campaign_management";
  const responseType = "code";

  const hasCalledApi = useRef(false);

  // Capture ALL callback data synchronously in the lazy initializer — runs
  // exactly once on the very first render, before any effects or re-renders.
  // Reading localStorage HERE ensures ads_sellerName / ads_marketplaceId are
  // still present (they're set just before the Amazon redirect), regardless of
  // how many re-renders happen before userData?.token becomes available.
  const [pendingCallback, setPendingCallback] = useState(() => {
    const url = new URL(window.location.href);
    const ad_code = url.searchParams.get("code");
    const ad_scope = url.searchParams.get("scope");
    if (ad_code && ad_scope) {
      // Snapshot localStorage values immediately — before any effect can clear them
      const seller_name = localStorage.getItem("ads_sellerName") || "";
      const marketplace_id = localStorage.getItem("ads_marketplaceId") || "";
      // Clear them now so they can't be picked up by a stale re-run
      localStorage.removeItem("ads_sellerName");
      localStorage.removeItem("ads_marketplaceId");
      if (window.history.replaceState) {
        window.history.replaceState({}, document.title, window.location.origin + window.location.pathname);
      }
      return { ad_code, ad_scope, seller_name, marketplace_id };
    }
    return null;
  });

  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [sellerName, setSellerName] = useState(localStorage.getItem("ads_sellerName") || "");
  const [marketplaceId, setMarketplaceId] = useState(localStorage.getItem("ads_marketplaceId") || "");
  const [adProfiles, setAdProfiles] = useState([]);
  const [selectedAdProfileId, setSelectedAdProfileId] = useState("");

  const marketplaces = [
    { id: "ATVPDKIKX0DER", name: "Amazon.com (US)" },
    { id: "A1AM78C64UM0Y8", name: "Amazon.com.mx (Mexico)" },
    { id: "A2EUQ1WTGCTBG2", name: "Amazon.ca (Canada)" },
    { id: "A1PA6795UKMFR9", name: "Amazon.de (Germany)" },
    { id: "A1RKKUPIHCS9HS", name: "Amazon.es (Spain)" },
    { id: "A13V1IB3VIYZZH", name: "Amazon.fr (France)" },
    { id: "A21TJRUUN4KGV", name: "Amazon.in (India)" },
    { id: "APJ6JRA9NG5V4", name: "Amazon.it (Italy)" },
    { id: "A1F83G8C2ARO7P", name: "Amazon.co.uk (UK)" },
    { id: "A1805IZSGTT6HS", name: "Amazon.nl (Netherlands)" },
    { id: "A2NODRKZP88ZB9", name: "Amazon.se (Sweden)" },
    { id: "A1C3SOZRARQ6R3", name: "Amazon.pl (Poland)" },
    { id: "A33AVAJ2PDY3EV", name: "Amazon.tr (Turkey)" },
    { id: "A39IBJ37TRP1C6", name: "Amazon.ae (UAE)" },
    { id: "A2VIGQ35RCS4UG", name: "Amazon.sa (Saudi Arabia)" },
    { id: "A19VAU5U5O7RUS", name: "Amazon.sg (Singapore)" },
    { id: "A17E79C6D8DWNP", name: "Amazon.com.au (Australia)" },
    { id: "A1VC38T7YXB528", name: "Amazon.co.jp (Japan)" },
    { id: "AAHKV2X7AFYLW", name: "Amazon.cn (China)" },
    { id: "ARBP9OOSHTCHU", name: "Amazon.com.br (Brazil)" },
  ];

  const handleDeleteCredentials = (id) => {
    Modal.confirm({
      title: 'Delete Advertising Credentials',
      content: 'Are you sure you want to delete these credentials? This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          const response = await MakeApiCall(`user-credentials/${id}`, 'delete', null, true, {}, userData?.token);
          if (response?.status) {
            message.success("Credentials deleted successfully");
            if (fetchList) await fetchList();
            else window.location.reload();
            try { await refreshAndUpdateAdsFlag(); } catch (e) { console.error('Error refreshing Ads flag after delete:', e); }
          } else {
            message.error(response?.message || "Failed to delete credentials");
          }
        } catch (error) {
          message.error("Something went wrong while deleting");
        }
      },
    });
  };

  const openLoginModal = () => setIsLoginModalOpen(true);
  const closeLoginModal = () => setIsLoginModalOpen(false);
  const closeProfileModal = () => setIsProfileModalOpen(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const triggerModal = urlParams.get('creds');
    if (triggerModal === 'true') {
      setIsLoginModalOpen(true);
      if (window.history.replaceState) {
        const cleanUrl = window.location.origin + window.location.pathname + window.location.search.replace(/&?openAdsModal=true/, '');
        window.history.replaceState({}, document.title, cleanUrl);
      }
    }
  }, []);

  const handleAmazonLogin = () => {
    localStorage.setItem("ads_sellerName", sellerName);
    localStorage.setItem("ads_marketplaceId", marketplaceId);
    const baseUrl = "https://www.amazon.com/ap/oa";
    const params = new URLSearchParams({
      client_id: clientId,
      scope,
      response_type: responseType,
      redirect_uri: redirectUri,
    });
    window.location.href = `${baseUrl}?${params.toString()}`;
  };

  const saveSelectedProfile = async () => {
    if (!selectedAdProfileId) {
      message.error("Please select an advertising profile");
      return;
    }
    try {
      await MakeApiCall(
        `advertising-credentials/save-advertising-profile`,
        "post",
        { ad_profile_ids: [selectedAdProfileId] },
        true,
        {},
        userData?.token
      );
      message.success("Advertising profile saved successfully");
      closeProfileModal();
      try { await refreshAndUpdateAdsFlag(); } catch (e) { console.error('Error refreshing Ads flag after saving profile:', e); }
      setTimeout(() => { window.location.reload(); }, 1000);
    } catch (err) {
      console.error("Error saving advertising profile:", err);
      message.error("Failed to save advertising profile");
    }
  };

  // ── Callback handler ──
  // Waits for userData?.token to be available before making the API call.
  // This fixes the first-time connection failure where the token wasn't yet
  // hydrated in Redux when the component first mounted after the Amazon redirect.
  useEffect(() => {
    if (!pendingCallback || !userData?.token || hasCalledApi.current) return;
    hasCalledApi.current = true;

    const { ad_code, ad_scope, seller_name, marketplace_id } = pendingCallback;
    setPendingCallback(null);

    const processCallback = async () => {
      try {
        const response = await MakeApiCall(
          `advertising-credentials/callback-url`,
          "post",
          {
            ad_code,
            scope: ad_scope,
            seller_name: seller_name || "Unknown Seller",
            marketplace_id: marketplace_id || "Unknown Marketplace",
          },
          true,
          {},
          userData?.token
        );

        let parsedData = [];
        try {
          parsedData = typeof response.data === "string" ? JSON.parse(response.data) : response.data;
        } catch (err) {
          console.error("Failed to parse response.data:", err);
        }

        if (response?.status && Array.isArray(parsedData) && parsedData.length > 0) {
          setAdProfiles(parsedData);
          if (parsedData.length === 1) setSelectedAdProfileId(parsedData[0].profileId.toString());
          setIsProfileModalOpen(true);
        } else if (response?.status) {
          message.warning("No advertising profiles found for the provided credentials.");
        } else {
          message.error("Failed to retrieve advertising profiles.");
        }

        localStorage.removeItem("ads_sellerName");
        localStorage.removeItem("ads_marketplaceId");

        try {
          await refreshAndUpdateAdsFlag();
        } catch (e) {
          console.error('Error refreshing after Ads callback:', e);
        }
      } catch (err) {
        console.error("Error in Ads API flow:", err);
        message.error("An error occurred during Amazon Ads login process.");
      }
    };

    processCallback();
  }, [pendingCallback, userData?.token]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = [
    // { title: 'Credential Type', dataIndex: 'credential_type', key: 'credential_type' },
    { 
      title: 'Seller Name', 
      dataIndex: 'seller_name', 
      key: 'seller_name',
      render: value => <div className='fw-bold'>{value}</div>
    },
    // { title: 'Region', dataIndex: 'region', key: 'region' },
    { title: 'Marketplace', dataIndex: 'marketplace', key: 'marketplace' },
    // { title: 'Marketplace ID', dataIndex: 'marketplace_id', key: 'marketplace_id' },
    // { title: 'Country Code', dataIndex: 'country_code', key: 'country_code' },
    { title: 'Currency Code', dataIndex: 'currency_code', key: 'currency_code' },
    { title: 'Time Zone', dataIndex: 'time_zone', key: 'time_zone' },
    { title: 'Profile ID', dataIndex: 'profile_id', key: 'profile_id' },
    {
      title: 'Expires On',
      dataIndex: 'credential_renewal_due_at',
      key: 'credential_renewal_due_at',
      render: (value) => {
          if (!value) return '-';
          // value assumed to be a unix timestamp (seconds)
          const date = new Date(value * 1000); 
          const now = new Date();
          const diffMs = date.getTime() - now.getTime();
          const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
          let daysRemain = '';
          if (diffDays > 1) {
              daysRemain = ` (${diffDays} days Remaining)`;
          } else if (diffDays === 1) {
              daysRemain = ` (1 day Remaining)`;
          } else if (diffDays === 0) {
              daysRemain = ` (expires today)`;
          } else {
              daysRemain = ` (expired)`;
          }
          return (
              <div>
                  <div>{date.toLocaleDateString()} {date.toLocaleTimeString()}</div>
                  <div style={{ fontSize: '12px', color: '#888' }}>{daysRemain}</div>
              </div>
          );
     
      }
  },
    {
      title: 'Action', key: 'action', width: 100, align: 'center',
      render: (_, record) => (
        <span className="cursor-pointer text-hover-danger" onClick={() => handleDeleteCredentials(record.id)}>
          <i className="bi bi-trash fs-3 text-danger"></i>
        </span>
      )
    }
  ];

  return (
    <Wrapper>
      <div className="row gy-5 g-xl-5">
        <div className="col-xxl-12">
          <div className="card card-xxl-stretch mb-5 mb-xl-8">
            <div className="card-header border-bottom-dashed">
              <h3 className="card-title fw-bolder">Campaign Manager Credentials List</h3>
              <div className="card-toolbar">
                {/* {(!list || list.length === 0) && (
                  <button className="btn btn-light-google" onClick={openLoginModal}>Login With Amazon</button>
                )} */}
              </div>
            </div>
            <div className="card-body px-3 py-3">
              {isMobile ? (
                <div>
                  {loading ? (
                    <div className="text-muted py-5 text-center">Loading...</div>
                  ) : !list || list.length === 0 ? (
                    <div className="text-muted py-5 text-center">No credentials found.</div>
                  ) : (
                    list.map((item) => (
                      <div key={item.id} className="card border mb-4">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-start mb-3">
                            <div className="fw-bolder">{item.seller_name || "-"}</div>
                            <span className="cursor-pointer text-hover-danger" onClick={() => handleDeleteCredentials(item.id)}>
                              <i className="bi bi-trash fs-3 text-danger"></i>
                            </span>
                          </div>
                          <div className="fs-7 mb-1"><span className="text-muted"><b>Credential Type:</b></span> <span className="text-dark">{item.credential_type || "-"}</span></div>
                          <div className="fs-7 mb-1"><span className="text-muted"><b>Region:</b></span> <span className="text-dark">{item.region || "-"}</span></div>
                          <div className="fs-7 mb-1"><span className="text-muted"><b>Marketplace:</b></span> <span className="text-dark">{item.marketplace || "-"}</span></div>
                          <div className="fs-7 mb-1"><span className="text-muted"><b>Marketplace ID:</b></span> <span className="text-dark">{item.marketplace_id || "-"}</span></div>
                          <div className="fs-7 mb-1"><span className="text-muted"><b>Country Code:</b></span> <span className="text-dark">{item.country_code || "-"}</span></div>
                          <div className="fs-7 mb-1"><span className="text-muted"><b>Currency Code:</b></span> <span className="text-dark">{item.currency_code || "-"}</span></div>
                          <div className="fs-7 mb-1"><span className="text-muted"><b>Time Zone:</b></span> <span className="text-dark">{item.time_zone || "-"}</span></div>
                          <div className="fs-7"><span className="text-muted"><b>Profile ID:</b></span> <span className="text-dark">{item.profile_id || "-"}</span></div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ) : (
                <div className="table-responsive">
                  <Table scroll={{ x: "max-content" }} columns={columns} loading={loading} dataSource={list} rowKey="id" pagination={false} 
                  locale={{
                    emptyText: (
                        <div className="text-muted py-5 text-center">
                           <button className="btn btn-light-google" onClick={openLoginModal}>
                                Login With Amazon
                            </button>
                        </div>
                    )
                }}
                />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        title="Amazon Ads Login"
        open={isLoginModalOpen}
        onCancel={closeLoginModal}
        onOk={handleAmazonLogin}
        okText="Continue"
        footer={[
          <button key="back" type="button" className="btn btn-light me-3" onClick={closeLoginModal}>Close</button>,
          <button
            key="submit"
            type="button"
            className="btn btn-danger"
            onClick={handleAmazonLogin}
            disabled={!sellerName.trim() || !marketplaceId}
          >
            Continue
          </button>
        ]}
        centered
      >
        <div className="mb-4">
          <label className="fw-bold mb-2">Seller Name</label>
          <Input value={sellerName} onChange={(e) => setSellerName(e.target.value)} placeholder="Enter Seller Name" className="py-2" />
        </div>
        <div className="mb-3">
          <label className="fw-bold mb-2">Marketplace</label>
          <Select showSearch style={{ width: "100%" }} value={marketplaceId || undefined} onChange={setMarketplaceId} placeholder="Select Marketplace" optionFilterProp="label">
            {marketplaces.map(m => (
              <Option key={m.id} value={m.id} label={m.name}>
                <div className="d-flex align-items-center">
                  <img src={`/assets/media/domainImage/${m.id}.png`} style={{ height: "18px", width: "24px", objectFit: "contain" }} loading="lazy" className="me-3" onError={(e) => { e.target.src = "/assets/media/domainImage/red-flag.png"; }} alt={m.name} />
                  <span>{m.name}</span>
                </div>
              </Option>
            ))}
          </Select>
        </div>
      </Modal>

      {/* ── ProfileSelectionModal now receives props instead of closing over stale state ── */}
      <ProfileSelectionModal
        isOpen={isProfileModalOpen}
        onCancel={closeProfileModal}
        adProfiles={adProfiles}
        selectedAdProfileId={selectedAdProfileId}
        setSelectedAdProfileId={setSelectedAdProfileId}
        onSave={saveSelectedProfile}
      />
    </Wrapper>
  );
}

export default AmazonAdvertising;